/**
 * Consolidated hook for organization/user context
 *
 * Provides:
 * - Current user's username and @-prefixed owner string
 * - Organization context for API calls (organizationId or slug)
 * - URL owner detection from pathname
 */

import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";
import { useOrganizations } from "@/lib/api";
import type { Organization as AuthOrganization, Session } from "@/lib/auth";
import { useAuthState } from "@/lib/auth-state";
import { getSubdomainOwner } from "@/lib/subdomain";
import { getOwnerFromPath, parseOwner } from "@/lib/url";
import { resolveOrgContext } from "./use-org-context.helpers";

export interface OrgContext {
	organizationId?: string;
	slug?: string;
}

interface UseOrgContextResult {
	// Session info
	session: Session | null;
	isAuthenticated: boolean;
	authReady: boolean;
	// Whether the `/api/organizations` query has settled at least once. The home
	// route must wait for this before falling back to /account/settings: a new
	// user's `currentOwner` is null until the org list loads, so deciding "no org"
	// before it settles strands them on Settings (see app/index.tsx).
	orgsReady: boolean;

	// Personal org slug (user's username)
	personalOrgSlug: string | null;

	// Active org
	activeOrg: AuthOrganization | null;
	activeOrgSlug: string | null;
	resolvedOrgSlug: string | null;
	resolvedOrganizationId: string | null;

	// URL-derived info
	urlOwner: string | null; // raw from URL (could be @user or org-slug)
	urlOrgSlug: string | null; // org slug only (null if @user URL)
	isUrlUserSpace: boolean;

	// Current context for display/navigation
	currentOwner: string | null; // what to use in URLs

	// API context - use this for API calls
	orgContext: OrgContext;
	hasOrgContext: boolean;

	// All orgs the current user belongs to (empty when unauthenticated)
	allOrgs: Array<{ id: string; name: string; slug: string }>;
}

export function useOrgContext(): UseOrgContextResult {
	const location = useLocation();
	const { session, isReady } = useAuthState();

	// `/api/organizations` returns both member and public orgs. The org context
	// only cares about member orgs (for fallback + the "active org" lookup), so
	// filter once and re-use. This replaces BetterAuth's `useActiveOrganization`
	// and `useListOrganizations` — those add two more XHRs to the initial-load
	// waterfall for the same data we already fetch here.
	const { data: allOrgs, isFetched: orgsFetched } = useOrganizations();
	const userOrgs = useMemo(
		() => (allOrgs ?? []).filter((o) => o.is_member),
		[allOrgs],
	);

	const personalOrgSlug =
		(session?.user as { username?: string | null })?.username || null;

	// Parse URL — subdomain wins over path on `{org}.lobu.ai` hosts.
	const urlOwner = getSubdomainOwner() ?? getOwnerFromPath(location.pathname);
	const urlOwnerInfo = urlOwner ? parseOwner(urlOwner) : null;
	const isUrlUserSpace = urlOwnerInfo?.isUser ?? false;
	const urlOrgSlug = isUrlUserSpace ? null : urlOwner;

	// The "active org" is the URL org if we have one and the user is a member.
	// Falls back to the first member org. This is the same role BetterAuth's
	// `useActiveOrganization` filled, derived from data we already have.
	const activeOrg = useMemo(() => {
		if (urlOrgSlug) {
			const match = userOrgs.find((o) => o.slug === urlOrgSlug);
			if (match) return match as unknown as AuthOrganization;
		}
		return (userOrgs[0] as unknown as AuthOrganization) ?? null;
	}, [userOrgs, urlOrgSlug]);

	const activeOrgSlug =
		(activeOrg as { slug?: string | null } | null)?.slug || null;
	const firstUserOrgSlug = userOrgs[0]?.slug || null;

	const {
		resolvedOrgSlug,
		resolvedOrganizationId,
		currentOwner,
		hasOrgContext,
	} = resolveOrgContext({
		isAuthenticated: !!session,
		activeOrgId: (activeOrg as { id?: string } | null)?.id,
		activeOrgSlug,
		urlOrgSlug,
		personalOrgSlug,
		firstUserOrgSlug,
	});

	// API context - what to pass to hooks
	const orgContext: OrgContext = session
		? {
				organizationId: resolvedOrganizationId || undefined,
				slug: resolvedOrgSlug || undefined,
			}
		: { slug: resolvedOrgSlug || undefined };

	return {
		session,
		isAuthenticated: !!session,
		authReady: isReady,
		orgsReady: orgsFetched,
		personalOrgSlug,
		activeOrg,
		activeOrgSlug,
		resolvedOrgSlug,
		resolvedOrganizationId,
		urlOwner,
		urlOrgSlug,
		isUrlUserSpace,
		currentOwner,
		orgContext,
		hasOrgContext,
		allOrgs: userOrgs as Array<{ id: string; name: string; slug: string }>,
	};
}
